import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { SectionItem, SectionWrapped } from './TopBarDropdownSectionItem.component'
import { Stack, Typography } from '@mui/material'
import { getContentLibraryLink, getHelpCenterLink } from 'utils/localization'

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import { AnalyticsEvent } from 'utils/analytics'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { Fragment } from 'react'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import { MUIDivider } from 'components/common/MUIDivider'
import { Path } from 'constants/router'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import type { TopbarProps } from './TopBarDropdown.component'
import { externalNavigate } from 'utils/helpers'
import i18n from 'translations/i18n'
import { useAppMeta } from 'components/higher-order/App/appMeta.context'
import { useAuth0 } from 'utils/auth'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

/**
 * @component TopBarDropdownInternalContent - Admin + Creative menu items for the TopBar dropdown
 */
export const TopBarDropdownInternalContent: React.FC<TopbarProps> = ({ setIsLanguageOpen, setIsContactUsOpen, closeMenuHandler, handleLogout, handleLogAnalyticsEvent }) => {
  const { t } = useTranslation(['topbar', 'authentication'])
  const navigate = useNavigate()
  const { isUserSet } = useAppMeta()
  const { user, roles } = useAuth0()

  return (
    <Fragment>
      <SectionWrapped>
        {isUserSet && (
          <Fragment>
            <SectionItem
              label={t('topbar:dashboard')}
              icon={<HomeOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
              onClick={() => {
                navigate(Path.INDEX)
                handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_DASHBOARD)
              }}
            />

            <SectionItem
              label={t('topbar:profile')}
              icon={<AccountCircleOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
              onClick={(e) => {
                navigate(Path.PROFILE)
                handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_PROFILE)
                closeMenuHandler(e)
              }}
            />
          </Fragment>
        )}

        <SectionItem
          label={t('topbar:language_settings')}
          icon={<ChatOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
          onClick={() => {
            handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_LANGUAGE)
            setIsLanguageOpen()
          }}
        />

        {roles.isCreative && (
          <SectionItem
            isExternalLink
            label={t('topbar:content_library')}
            icon={<LockOpenOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
            onClick={() => {
              externalNavigate(getContentLibraryLink(i18n.language))
            }}
          />
        )}
      </SectionWrapped>

      <MUIDivider margin={12} sx={{ width: '100%' }} />

      {roles.isAdmin && (
        <Fragment>
          <SectionWrapped>
            <SectionItem isLight label="API" icon={<DataObjectOutlinedIcon sx={{ fontSize: '1.8rem' }} />} onClick={() => navigate(Path.API)} />

            <SectionItem isLight label="Client onboarding" icon={<PersonAddAltOutlinedIcon sx={{ fontSize: '1.8rem' }} />} onClick={() => navigate(Path.ADMIN_CLIENT_ONBOARDING)} />

            <SectionItem isLight label="Creative onboarding" icon={<CameraAltOutlinedIcon sx={{ fontSize: '1.8rem' }} />} onClick={() => navigate(Path.ADMIN_CREATIVE_ONBOARDING)} />
          </SectionWrapped>

          <MUIDivider margin={12} sx={{ width: '100%' }} />

          <SectionWrapped>
            <SectionItem
              isLight
              isExternalLink
              label={t('topbar:help_center')}
              icon={<HelpOutlineOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
              onClick={() => {
                externalNavigate(getHelpCenterLink(i18n.language))
              }}
            />
          </SectionWrapped>

          <MUIDivider margin={12} sx={{ width: '100%' }} />
        </Fragment>
      )}

      {!roles.isAdmin && (
        <Fragment>
          <SectionWrapped>
            {isUserSet && (
              <SectionItem
                isLight
                isExternalLink
                label={t('topbar:help_center')}
                icon={<HelpOutlineOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
                onClick={() => {
                  externalNavigate(getHelpCenterLink(i18n.language))
                  handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_HELP_CENTER)
                }}
              />
            )}

            <SectionItem
              isLight
              label={t('topbar:terms_and_privacy')}
              icon={<DescriptionOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
              onClick={() => {
                navigate(Path.TERMS_AND_PRIVACY)
                handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_TERMS_AND_POLICY)
              }}
            />

            {isUserSet && (
              <SectionItem
                isLight
                label={t('topbar:contact_us')}
                icon={<PhoneOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
                onClick={() => {
                  handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_CONTACT_US)
                  setIsContactUsOpen()
                }}
              />
            )}
          </SectionWrapped>

          <MUIDivider margin={12} sx={{ width: '100%' }} />
        </Fragment>
      )}

      <SectionWrapped>
        <Stack
          gap=".2rem"
          padding="0 .4rem"
          sx={{ cursor: 'pointer' }}
          onClick={() => {
            navigate(Path.PROFILE)
            handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_USER_PROFILE)
          }}
        >
          <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
            {user?.name}
          </Typography>

          <Typography variant="text-xs" fontWeight={350} color={GRAY_700}>
            {user?.email}
          </Typography>
        </Stack>

        <SectionItem
          isLight
          label={t('authentication:log_out')}
          icon={<LogoutOutlinedIcon sx={{ fontSize: '1.8rem' }} />}
          onClick={(e) => {
            handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU_LOG_OUT)
            handleLogout(e)
          }}
        />
      </SectionWrapped>
    </Fragment>
  )
}
